/**
* Hand-authored per-locale copy for /{locale}/visa pillar mirrors.
* Unique (no MT). Evidence-led guidance; do not claim a word count without measuring.
* Locales: en · zh · ja · ko · vi · id.
*/
export type VisaLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface VisaLocaleCopy {
  hreflang: string;
  title: string;
  description: string;
  h1: string;
  lead: string;
  benefits: Array<[string, string]>;
  sections: Array<{ heading: string; paragraphs: string[] }>;
  faqs: Array<{ q: string; a: string }>;
  ctaCall: string;
  ctaLine: string;
  breadcrumbHome: string;
  breadcrumbVisa: string;
  seeAllServices: string;
  seeAllCountries: string;
}

export const VISA_LOCALE_COPY: Record<VisaLocale, VisaLocaleCopy> = {
  "en": {
    "hreflang": "en",
    "title": "Thailand Visa Guidance — Documents & Eligibility | Thai Notary Law",
    "description": "Compare Thai visa routes, required evidence and filing authorities for work, family, retirement and long stays. Check official criteria before requesting a quote.",
    "h1": "Thailand Visa & Immigration — Choose the Right Application",
    "lead": "Start with your nationality, actual country of residence, travel purpose and current immigration permission. Applying for a visa abroad, extending a stay in Thailand and obtaining authorization to work are separate processes. Thai Notary Law can assess the requested scope of assistance; the competent authority decides eligibility, additional evidence and approval. This page is general guidance, not confirmation that an individual qualifies.",
    "benefits": [
      [
        "Choose the route before collecting documents",
        "TR relates to tourism; Non-B may relate to business or employment, but a visa is not itself general work authorization. Family and retirement routes must be checked against their own criteria. Do not assume Non-O, O-A and O-X share one insurance or financial checklist."
      ],
      [
        "Evidence and foreign documents",
        "Use the checklist issued by the embassy or office receiving the actual application. Passport details, residence evidence, employment or school documents, civil-status records and financial evidence serve different purposes; not every category requires every document. A checklist for a different country or visa may be unsuitable."
      ],
      [
        "Costs, timing and obligations after approval",
        "Request an itemised quote separating professional fees, authority fees, translations and third-party costs. Processing estimates depend on the receiving authority, document completeness, appointment availability and additional checks. No adviser can guarantee an approval date or result."
      ]
    ],
    "sections": [
      {
        "heading": "Choose the route before collecting documents",
        "paragraphs": [
          "TR relates to tourism; Non-B may relate to business or employment, but a visa is not itself general work authorization. Family and retirement routes must be checked against their own criteria. Do not assume Non-O, O-A and O-X share one insurance or financial checklist.",
          "SMART and LTR require assessment under the relevant BOI program. DTV depends on the application purpose and supporting evidence. Thailand Privilege is a membership program, not permanent residence or general permission to work. Confirm the current program terms before committing funds.",
          "Permanent Residence is separate from annual extensions and nationality. Check the current application window, category, residence history and evidence with the Immigration Bureau. Length of residence alone does not guarantee acceptance. Maintain lawful status while the application is considered."
        ]
      },
      {
        "heading": "Evidence and foreign documents",
        "paragraphs": [
          "Use the checklist issued by the embassy or office receiving the actual application. Passport details, residence evidence, employment or school documents, civil-status records and financial evidence serve different purposes; not every category requires every document. A checklist for a different country or visa may be unsuitable.",
          "Check that names, dates and supporting relationships are consistent before commissioning translations. Ask the receiving authority which language and certification route it accepts. Notarisation, consular legalisation and embassy certification are distinct; none independently establishes visa eligibility. For BOI cases, check the company’s approved project, position and applicant rather than assuming promotion covers every employee."
        ]
      },
      {
        "heading": "Costs, timing and obligations after approval",
        "paragraphs": [
          "Request an itemised quote separating professional fees, authority fees, translations and third-party costs. Processing estimates depend on the receiving authority, document completeness, appointment availability and additional checks. No adviser can guarantee an approval date or result.",
          "After approval, distinguish visa validity from the period of stay granted on entry. Check the actual entry stamp, work conditions, accommodation reporting, periodic reporting and any re-entry requirement under your status. Do not assume every visa has the same reporting exceptions or that applying for an extension automatically preserves permission."
        ]
      }
    ],
    "faqs": [
      {
        "q": "Can I change to Non-B inside Thailand?",
        "a": "Check your current status, remaining permitted stay, employer evidence and the receiving office’s current requirements. A change is not available in every case; do not rely on a universal day threshold or assume any consulate will accept you."
      },
      {
        "q": "Does Thailand Privilege include permission to work?",
        "a": "Membership must not be treated as general work authorization. Assess the actual activity and applicable immigration and work-permission rules separately."
      },
      {
        "q": "What should dependants check?",
        "a": "The principal holder’s status and the relationship must meet the relevant program’s criteria. Age limits, evidence and application procedures may differ between ordinary Non-O, SMART, LTR and BOI cases."
      },
      {
        "q": "Are all foreign documents required to be legalised?",
        "a": "Ask the actual receiving authority. The required language and certification depend on the document and application; do not purchase an assumed certification chain first."
      },
      {
        "q": "What happens after a refusal?",
        "a": "Read the reasons and available procedures. Review, additional evidence or a new application may be possible depending on the channel, but there is no universal appeal route or guaranteed later approval."
      },
      {
        "q": "Can someone submit reports for me?",
        "a": "Confirm whether representation or online submission is allowed for your specific report and status. Agree the scope, required authorization and receipt handling before engaging assistance."
      }
    ],
    "ctaCall": "Request a case-specific assessment",
    "ctaLine": "Provide your application purpose, country of residence and current permission dates through the office contact page.",
    "breadcrumbHome": "Home",
    "breadcrumbVisa": "Visa",
    "seeAllServices": "Visa application routes",
    "seeAllCountries": "Nationality navigation"
  },
  "zh": {
    "hreflang": "zh",
    "title": "泰国签证申请指南 — 材料与资格核对 | Thai Notary Law",
    "description": "比较泰国工作、家庭、退休和长期停留签证的申请路径，核对材料、受理机关及注意事项；先确认官方条件，再申请个案报价。",
    "h1": "泰国签证与移民 — 先确认适合的申请路径",
    "lead": "先明确国籍、实际居住国家、赴泰目的以及目前的停留许可。境外申请签证、在泰延长停留和取得工作许可属于不同程序。Thai Notary Law 可根据情况评估协助范围，资格、补充材料及批准结果由主管机关决定。本页为一般资料，不确认任何个人必然符合申请条件。",
    "benefits": [
      [
        "先选申请依据，再整理材料",
        "TR 主要涉及旅游；Non-B 可涉及商务或就业，但持有签证不等于获得一般工作许可。家庭和退休路径需分别核对。Non-O、O-A 与 O-X 的保险、财务和申请地点条件不能混用。"
      ],
      [
        "境外文件与证明目的",
        "以实际受理使领馆或机关的清单为准。护照、居住证明、雇主或学校材料、家庭关系与资金证据各有用途，并非每类签证都需要全部材料。其他国家或其他签证的清单不能直接套用。"
      ],
      [
        "费用、审理时间及批准后的义务",
        "报价应区分服务费、官方费用、翻译和第三方支出。审理时间取决于材料完整性、预约、排队及额外核查。服务方不能保证批准日期或结果。"
      ]
    ],
    "sections": [
      {
        "heading": "先选申请依据，再整理材料",
        "paragraphs": [
          "TR 主要涉及旅游；Non-B 可涉及商务或就业，但持有签证不等于获得一般工作许可。家庭和退休路径需分别核对。Non-O、O-A 与 O-X 的保险、财务和申请地点条件不能混用。",
          "SMART 与 LTR 应按 BOI 对应项目核查资格。DTV 需证明符合的活动或工作目的。Thailand Privilege 是会员项目，不等于永久居留或一般工作权。付款前应阅读现行会员条件。",
          "永久居留 PR 与年度延期及入籍不同。应核对当期开放申请公告、申请类别、居留记录与证明材料。长期在泰并不自动获得批准；等待期间仍须按主管机关要求维持合法状态。"
        ]
      },
      {
        "heading": "境外文件与证明目的",
        "paragraphs": [
          "以实际受理使领馆或机关的清单为准。护照、居住证明、雇主或学校材料、家庭关系与资金证据各有用途，并非每类签证都需要全部材料。其他国家或其他签证的清单不能直接套用。",
          "翻译前核对姓名、日期及文件间的关系。向受理机关确认语言与认证要求；公证、领事认证和使馆认证作用不同，不能单独证明签证资格。BOI 案件还应核对公司获准项目、职位及个人，而非认为所有外籍员工均获覆盖。"
        ]
      },
      {
        "heading": "费用、审理时间及批准后的义务",
        "paragraphs": [
          "报价应区分服务费、官方费用、翻译和第三方支出。审理时间取决于材料完整性、预约、排队及额外核查。服务方不能保证批准日期或结果。",
          "获批后区分签证有效期与入境章准许停留期限，并按实际身份核对工作条件、住宿申报、定期报告及再入境要求。不同签证的豁免可能不同，提交延期申请不应被视为自动保留停留权。"
        ]
      }
    ],
    "faqs": [
      {
        "q": "能在泰国境内改为 Non-B 吗？",
        "a": "应核对现有身份、剩余停留期、雇主文件及受理机关现行规则；并非每个个案都能境内转换，不应依据统一天数或假定任何领馆都受理。"
      },
      {
        "q": "Thailand Privilege 包含工作许可吗？",
        "a": "不可将会员身份当作一般工作授权；需按实际活动分别核对移民和工作许可规定。"
      },
      {
        "q": "家属申请应核对什么？",
        "a": "主申请人身份及关系必须符合相应项目条件。普通 Non-O、SMART、LTR 与 BOI 的年龄限制、证据及程序可能不同。"
      },
      {
        "q": "所有境外材料都须认证吗？",
        "a": "先问实际受理机关。语言与认证路线取决于材料及申请，不应预先购买假定必需的认证服务。"
      },
      {
        "q": "拒签后如何处理？",
        "a": "阅读理由及可用程序，可能需要补充证据、申请复核或重新申请；并非每类都有复议渠道，也不能保证下次批准。"
      },
      {
        "q": "报告可委托代交吗？",
        "a": "应确认特定身份和报告是否允许代理或电子提交，并在委托前确定授权、服务范围及回执处理。"
      }
    ],
    "ctaCall": "申请个案评估与报价",
    "ctaLine": "请通过事务所联系页面说明申请目的、实际居住国家和当前停留期限。",
    "breadcrumbHome": "首页",
    "breadcrumbVisa": "签证",
    "seeAllServices": "签证申请路径",
    "seeAllCountries": "国籍导航"
  },
  "ja": {
    "hreflang": "ja",
    "title": "タイビザ申請ガイド — 書類と資格確認 | Thai Notary Law",
    "description": "就労・家族・退職・長期滞在のタイビザを比較し、必要書類、申請先、注意点を確認。公式条件を確認してから個別見積りをご相談ください。",
    "h1": "タイビザ・入国管理 — 申請経路の確認",
    "lead": "国籍、実際の居住国、渡航目的、現在の滞在許可から確認します。国外でのビザ申請、タイ国内の滞在延長、就労許可は別の手続です。Thai Notary Law は事情に応じた支援範囲を検討できますが、資格、追加資料、許可の判断は管轄当局が行います。本ページは一般情報であり、個人の適格性を保証するものではありません。",
    "benefits": [
      [
        "書類収集の前に申請の根拠を選ぶ",
        "TR は観光、Non-B は事業や雇用に関連しますが、ビザだけで一般的な就労権が得られるわけではありません。家族と退職の申請はそれぞれ確認し、Non-O、O-A、O-X の保険や資金の条件を混同しないでください。"
      ],
      [
        "外国文書と証明の目的",
        "実際に受理する大使館または当局の書類一覧を使います。旅券、居住証明、雇用や学校の資料、身分関係、資金資料の目的は異なり、全てが全申請に必要とは限りません。別の国やビザの一覧をそのまま流用しないでください。"
      ],
      [
        "費用・審査期間・許可後の義務",
        "見積りでは専門家費用、公的手数料、翻訳、第三者費用を分けます。審査期間は書類の完成度、予約、待ち順、追加確認によります。支援者が許可日や結果を保証することはできません。"
      ]
    ],
    "sections": [
      {
        "heading": "書類収集の前に申請の根拠を選ぶ",
        "paragraphs": [
          "TR は観光、Non-B は事業や雇用に関連しますが、ビザだけで一般的な就労権が得られるわけではありません。家族と退職の申請はそれぞれ確認し、Non-O、O-A、O-X の保険や資金の条件を混同しないでください。",
          "SMART と LTR は BOI の該当プログラムの資格確認が必要です。DTV は対象となる目的と証拠に基づきます。Thailand Privilege は会員制度であり、永住権や一般的な就労許可ではありません。支払う前に最新の条件を確認します。",
          "PR は毎年の滞在延長や国籍取得とは別です。当期の受付告示、申請区分、滞在履歴と証拠を入国管理局に確認します。滞在年数だけで許可されるものではなく、審査中も当局の指示に従って適法な滞在を維持する必要があります。"
        ]
      },
      {
        "heading": "外国文書と証明の目的",
        "paragraphs": [
          "実際に受理する大使館または当局の書類一覧を使います。旅券、居住証明、雇用や学校の資料、身分関係、資金資料の目的は異なり、全てが全申請に必要とは限りません。別の国やビザの一覧をそのまま流用しないでください。",
          "翻訳前に氏名、日付、書類間の関係を確認します。受理先に言語と認証経路を確認してください。公証、領事認証、大使館認証は機能が異なり、それ自体でビザ資格を証明しません。BOI 案件は承認事業、職位、個人を確認し、全外国人社員が対象と推定しないことが重要です。"
        ]
      },
      {
        "heading": "費用・審査期間・許可後の義務",
        "paragraphs": [
          "見積りでは専門家費用、公的手数料、翻訳、第三者費用を分けます。審査期間は書類の完成度、予約、待ち順、追加確認によります。支援者が許可日や結果を保証することはできません。",
          "許可後もビザの有効期間と入国時に認められた滞在期間を区別します。実際の入国スタンプ、就労条件、住所届、定期報告、再入国手続を確認します。免除条件はビザごとに異なり、延長申請だけで滞在権が自動的に維持されると判断しないでください。"
        ]
      }
    ],
    "faqs": [
      {
        "q": "タイ国内で Non-B に変更できますか？",
        "a": "現在の資格、残りの滞在期間、雇用主資料、受理先の現行条件を確認します。全てのケースで変更できるわけではなく、一律の日数や任意の領事館での受理を前提にできません。"
      },
      {
        "q": "Thailand Privilege に就労許可は含まれますか？",
        "a": "会員資格を一般的な就労権と扱わず、実際の活動に適用される滞在・就労規則を別に確認します。"
      },
      {
        "q": "家族申請で確認すべきことは？",
        "a": "主申請者の資格と家族関係が対象制度の条件に合うか確認します。Non-O、SMART、LTR、BOI では年齢制限、資料、手続が異なる場合があります。"
      },
      {
        "q": "外国の書類は全て認証が必要ですか？",
        "a": "実際の受理先に確認します。言語と認証は資料及び申請により異なるため、先に認証経路を決めつけないでください。"
      },
      {
        "q": "不許可になった場合は？",
        "a": "理由と利用できる手続を確認します。追加証拠、再検討、新規申請の可能性は窓口次第で、共通の不服申立て経路や次回許可の保証はありません。"
      },
      {
        "q": "報告の代理提出は可能ですか？",
        "a": "実際の資格と報告について代理やオンライン提出が可能か確認し、委任範囲、必要な権限、受領書の取扱いを合意します。"
      }
    ],
    "ctaCall": "個別の申請評価と見積りを相談",
    "ctaLine": "事務所の連絡ページから目的、居住国、現在の滞在期限をお知らせください。",
    "breadcrumbHome": "ホーム",
    "breadcrumbVisa": "ビザ",
    "seeAllServices": "ビザの申請経路",
    "seeAllCountries": "国籍別の案内"
  },
  "ko": {
    "hreflang": "ko",
    "title": "태국 비자 신청 안내 — 서류와 자격 확인 | Thai Notary Law",
    "description": "취업·가족·은퇴·장기 체류 비자의 신청 경로, 증빙과 접수기관을 비교하세요. 공식 조건을 확인한 후 개별 업무 범위와 견적을 상담할 수 있습니다.",
    "h1": "태국 비자·출입국 — 신청 경로부터 확인",
    "lead": "국적, 실제 거주국, 방문 목적과 현재 체류 허가를 먼저 확인합니다. 해외 비자 신청, 태국 내 체류 연장, 취업 허가는 별도 절차입니다. Thai Notary Law는 사정에 맞는 지원 범위를 검토할 수 있지만 자격, 추가 자료와 승인 여부는 담당 기관이 결정합니다. 이 페이지는 일반 정보이며 개인의 신청 자격을 보장하지 않습니다.",
    "benefits": [
      [
        "서류를 모으기 전에 신청 근거 선택",
        "TR은 관광, Non-B는 사업 또는 고용과 관련되지만 비자 자체가 일반적인 취업 허가는 아닙니다. 가족과 은퇴 신청은 각자 조건을 확인해야 하며 Non-O, O-A, O-X의 보험·재정 요건을 혼용하면 안 됩니다."
      ],
      [
        "해외 서류와 증빙 목적",
        "실제 접수 대사관이나 기관의 서류 목록을 사용합니다. 여권, 거주 증명, 고용·학교 서류, 가족관계와 재정 자료는 목적이 다르며 모든 신청에 모두 필요한 것은 아닙니다. 다른 국가나 비자 목록을 그대로 적용하지 마세요."
      ],
      [
        "비용·심사 기간·승인 후 의무",
        "견적에서 전문 서비스 비용, 공식 수수료, 번역과 제삼자 비용을 구분합니다. 심사 기간은 서류 완성도, 예약, 대기와 추가 확인에 따라 달라집니다. 지원자가 승인일이나 결과를 보장할 수는 없습니다."
      ]
    ],
    "sections": [
      {
        "heading": "서류를 모으기 전에 신청 근거 선택",
        "paragraphs": [
          "TR은 관광, Non-B는 사업 또는 고용과 관련되지만 비자 자체가 일반적인 취업 허가는 아닙니다. 가족과 은퇴 신청은 각자 조건을 확인해야 하며 Non-O, O-A, O-X의 보험·재정 요건을 혼용하면 안 됩니다.",
          "SMART와 LTR은 BOI의 해당 프로그램 기준으로 자격을 확인합니다. DTV는 해당 목적과 증빙에 근거합니다. Thailand Privilege는 회원제 프로그램으로 영주권이나 일반적인 취업 권한이 아닙니다. 결제 전에 현행 조건을 읽어야 합니다.",
          "PR은 연례 체류 연장 및 국적 취득과 별도입니다. 현행 접수 공고, 신청 유형, 체류 이력과 증빙을 이민국에서 확인합니다. 오래 거주했다는 이유만으로 승인되지 않으며 심사 중에도 담당 기관의 지시에 따라 적법한 상태를 유지해야 합니다."
        ]
      },
      {
        "heading": "해외 서류와 증빙 목적",
        "paragraphs": [
          "실제 접수 대사관이나 기관의 서류 목록을 사용합니다. 여권, 거주 증명, 고용·학교 서류, 가족관계와 재정 자료는 목적이 다르며 모든 신청에 모두 필요한 것은 아닙니다. 다른 국가나 비자 목록을 그대로 적용하지 마세요.",
          "번역 전에 이름, 날짜와 문서 간 관계를 확인합니다. 접수기관에 언어와 인증 경로를 문의하세요. 공증, 영사 인증과 대사관 인증은 기능이 다르며 그 자체로 비자 자격을 증명하지 않습니다. BOI 신청은 회사의 승인 사업, 직위와 개인을 확인해야 하며 모든 외국인 직원이 포함된다고 추정하지 않습니다."
        ]
      },
      {
        "heading": "비용·심사 기간·승인 후 의무",
        "paragraphs": [
          "견적에서 전문 서비스 비용, 공식 수수료, 번역과 제삼자 비용을 구분합니다. 심사 기간은 서류 완성도, 예약, 대기와 추가 확인에 따라 달라집니다. 지원자가 승인일이나 결과를 보장할 수는 없습니다.",
          "비자 유효기간과 입국 시 허가된 체류 기간은 구분합니다. 실제 입국 도장, 취업 조건, 숙소 신고, 정기 보고와 재입국 요건을 확인하세요. 면제는 비자마다 다를 수 있으며 연장 신청만으로 체류 권한이 자동 유지된다고 판단하면 안 됩니다."
        ]
      }
    ],
    "faqs": [
      {
        "q": "태국 안에서 Non-B로 변경할 수 있나요?",
        "a": "현재 신분, 남은 체류 기간, 고용주 증빙과 접수기관 현행 조건을 확인합니다. 모든 경우에 가능하지 않으며 일률적인 잔여 일수나 어느 영사관에서나 접수된다는 가정을 피해야 합니다."
      },
      {
        "q": "Thailand Privilege에 취업 허가가 포함되나요?",
        "a": "회원 자격을 일반적인 취업 권한으로 보지 말고 실제 활동에 적용되는 출입국 및 취업 규칙을 별도로 확인하세요."
      },
      {
        "q": "동반 가족은 무엇을 확인하나요?",
        "a": "주신청자의 상태와 관계가 해당 프로그램 조건에 맞아야 합니다. 일반 Non-O, SMART, LTR과 BOI는 연령, 자료와 절차가 다를 수 있습니다."
      },
      {
        "q": "해외 서류는 모두 인증해야 하나요?",
        "a": "실제 접수기관에 문의하세요. 언어와 인증은 서류 및 신청에 따라 다르므로 먼저 인증 경로를 단정하지 않습니다."
      },
      {
        "q": "거절 후에는 어떻게 하나요?",
        "a": "사유와 이용 가능한 절차를 읽습니다. 추가 증빙, 재검토나 새 신청은 경로에 따라 가능할 수 있지만 공통 이의 절차나 다음 승인 보장은 없습니다."
      },
      {
        "q": "보고를 대리 제출할 수 있나요?",
        "a": "해당 신분과 보고에 대리 또는 온라인 제출이 허용되는지 확인하고 위임 범위, 권한과 접수증 처리를 합의하세요."
      }
    ],
    "ctaCall": "개별 신청 평가와 견적 상담",
    "ctaLine": "사무소 연락 페이지에서 신청 목적, 실제 거주국과 현재 체류 만료일을 알려주세요.",
    "breadcrumbHome": "홈",
    "breadcrumbVisa": "비자",
    "seeAllServices": "비자 신청 경로",
    "seeAllCountries": "국적별 안내"
  },
  "vi": {
    "hreflang": "vi",
    "title": "Visa Thái Lan — Kiểm tra hồ sơ và điều kiện | Thai Notary Law",
    "description": "So sánh đường nộp visa làm việc, gia đình, nghỉ hưu và dài hạn. Kiểm tra chứng cứ, cơ quan tiếp nhận và điều kiện chính thức trước khi xin báo giá theo hồ sơ.",
    "h1": "Visa và di trú Thái Lan — Chọn đúng đường nộp",
    "lead": "Bắt đầu từ quốc tịch, nước cư trú thực tế, mục đích đến Thái Lan và giấy phép lưu trú hiện có. Xin visa ở nước ngoài, gia hạn lưu trú tại Thái Lan và quyền làm việc là các thủ tục riêng. Thai Notary Law có thể đánh giá phạm vi hỗ trợ; cơ quan có thẩm quyền quyết định điều kiện, chứng cứ bổ sung và kết quả. Trang này là thông tin chung, không xác nhận cá nhân chắc chắn đủ điều kiện.",
    "benefits": [
      [
        "Chọn căn cứ trước khi gom hồ sơ",
        "TR liên quan du lịch; Non-B có thể liên quan kinh doanh hoặc việc làm, nhưng visa không tự tạo quyền làm việc chung. Hồ sơ gia đình và nghỉ hưu cần kiểm tra riêng; không dùng chung điều kiện bảo hiểm và tài chính của Non-O, O-A và O-X."
      ],
      [
        "Chứng cứ và giấy tờ nước ngoài",
        "Dùng danh mục của đại sứ quán hoặc cơ quan thực sự nhận hồ sơ. Hộ chiếu, chứng cứ cư trú, giấy tờ chủ lao động hay trường học, quan hệ gia đình và tài chính có mục đích khác nhau; không phải diện nào cũng cần tất cả. Không áp dụng nguyên danh mục của nước hoặc loại visa khác."
      ],
      [
        "Chi phí, thời gian và nghĩa vụ sau khi được chấp thuận",
        "Báo giá nên tách phí dịch vụ, phí cơ quan, dịch thuật và chi phí bên thứ ba. Thời gian tùy độ đầy đủ của hồ sơ, lịch hẹn, hàng đợi và kiểm tra bổ sung. Người hỗ trợ không thể bảo đảm ngày duyệt hay kết quả."
      ]
    ],
    "sections": [
      {
        "heading": "Chọn căn cứ trước khi gom hồ sơ",
        "paragraphs": [
          "TR liên quan du lịch; Non-B có thể liên quan kinh doanh hoặc việc làm, nhưng visa không tự tạo quyền làm việc chung. Hồ sơ gia đình và nghỉ hưu cần kiểm tra riêng; không dùng chung điều kiện bảo hiểm và tài chính của Non-O, O-A và O-X.",
          "SMART và LTR phải đối chiếu chương trình tương ứng của BOI. DTV dựa trên mục đích và chứng cứ phù hợp. Thailand Privilege là chương trình thành viên, không phải thường trú hay quyền làm việc chung. Đọc điều kiện hiện hành trước khi thanh toán.",
          "PR khác với gia hạn hằng năm và nhập quốc tịch. Kiểm tra đợt tiếp nhận hiện tại, nhóm hồ sơ, lịch sử cư trú và chứng cứ tại Cục Xuất nhập cảnh. Sống lâu tại Thái Lan không tự bảo đảm được duyệt; phải giữ tình trạng hợp pháp theo hướng dẫn của cơ quan khi chờ kết quả."
        ]
      },
      {
        "heading": "Chứng cứ và giấy tờ nước ngoài",
        "paragraphs": [
          "Dùng danh mục của đại sứ quán hoặc cơ quan thực sự nhận hồ sơ. Hộ chiếu, chứng cứ cư trú, giấy tờ chủ lao động hay trường học, quan hệ gia đình và tài chính có mục đích khác nhau; không phải diện nào cũng cần tất cả. Không áp dụng nguyên danh mục của nước hoặc loại visa khác.",
          "Trước khi dịch, kiểm tra tên, ngày tháng và mối liên hệ giữa tài liệu. Hỏi nơi nhận về ngôn ngữ và tuyến xác thực. Công chứng, chứng nhận lãnh sự và chứng nhận đại sứ quán có chức năng khác nhau, không tự chứng minh đủ điều kiện visa. Với BOI phải kiểm tra dự án, vị trí và cá nhân được chấp thuận, không mặc định mọi nhân viên nước ngoài đều được bao phủ."
        ]
      },
      {
        "heading": "Chi phí, thời gian và nghĩa vụ sau khi được chấp thuận",
        "paragraphs": [
          "Báo giá nên tách phí dịch vụ, phí cơ quan, dịch thuật và chi phí bên thứ ba. Thời gian tùy độ đầy đủ của hồ sơ, lịch hẹn, hàng đợi và kiểm tra bổ sung. Người hỗ trợ không thể bảo đảm ngày duyệt hay kết quả.",
          "Phân biệt thời hạn visa với thời gian được phép ở sau nhập cảnh. Kiểm tra dấu nhập cảnh thực tế, điều kiện làm việc, khai báo chỗ ở, báo cáo định kỳ và tái nhập cảnh theo diện cụ thể. Các ngoại lệ không giống nhau; nộp gia hạn không nên được coi là tự động duy trì quyền lưu trú."
        ]
      }
    ],
    "faqs": [
      {
        "q": "Có thể đổi sang Non-B trong Thái Lan không?",
        "a": "Cần kiểm tra tình trạng hiện tại, thời gian ở còn lại, chứng cứ chủ lao động và quy định của nơi nhận. Không phải trường hợp nào cũng được đổi; tránh dùng mốc ngày chung hoặc giả định mọi lãnh sự quán đều tiếp nhận."
      },
      {
        "q": "Thailand Privilege có quyền làm việc không?",
        "a": "Không coi tư cách thành viên là quyền làm việc chung. Kiểm tra riêng hoạt động thực tế với quy định nhập cư và lao động áp dụng."
      },
      {
        "q": "Người phụ thuộc cần kiểm tra gì?",
        "a": "Tình trạng người giữ visa chính và quan hệ phải phù hợp chương trình. Non-O thông thường, SMART, LTR và BOI có thể khác về tuổi, chứng cứ và thủ tục."
      },
      {
        "q": "Mọi giấy tờ nước ngoài đều cần xác thực không?",
        "a": "Hỏi cơ quan thực sự nhận hồ sơ. Ngôn ngữ và xác thực phụ thuộc giấy tờ và loại đơn, không nên mua trước một tuyến xác thực giả định."
      },
      {
        "q": "Nếu bị từ chối thì sao?",
        "a": "Đọc lý do và thủ tục có sẵn. Bổ sung chứng cứ, đề nghị xem xét hoặc nộp mới tùy kênh; không có đường khiếu nại chung hay bảo đảm lần sau được duyệt."
      },
      {
        "q": "Có thể ủy quyền nộp báo cáo không?",
        "a": "Xác nhận báo cáo và diện lưu trú cụ thể có cho phép đại diện hoặc nộp trực tuyến, rồi thống nhất phạm vi, giấy ủy quyền và xử lý biên nhận."
      }
    ],
    "ctaCall": "Xin đánh giá và báo giá theo hồ sơ",
    "ctaLine": "Gửi mục đích, nước cư trú thực tế và ngày hết hạn lưu trú qua trang liên hệ của văn phòng.",
    "breadcrumbHome": "Trang chủ",
    "breadcrumbVisa": "Visa",
    "seeAllServices": "Các đường nộp visa",
    "seeAllCountries": "Điều hướng theo quốc tịch"
  },
  "id": {
    "hreflang": "id",
    "title": "Visa Thailand — Dokumen & Kelayakan | Thai Notary Law",
    "description": "Bandingkan jalur visa kerja, keluarga, pensiun dan tinggal panjang. Periksa bukti, instansi penerima dan syarat resmi sebelum meminta penawaran sesuai kasus.",
    "h1": "Visa & Imigrasi Thailand — Tentukan Jalur Permohonan",
    "lead": "Mulai dari kewarganegaraan, negara tempat tinggal sebenarnya, tujuan perjalanan dan izin tinggal saat ini. Visa dari luar negeri, perpanjangan tinggal di Thailand dan izin bekerja merupakan proses terpisah. Thai Notary Law dapat menilai lingkup bantuan; instansi berwenang menentukan kelayakan, bukti tambahan dan persetujuan. Halaman ini adalah informasi umum, bukan konfirmasi kelayakan pribadi.",
    "benefits": [
      [
        "Pilih dasar permohonan sebelum mengumpulkan dokumen",
        "TR terkait wisata; Non-B dapat terkait bisnis atau pekerjaan, tetapi visa bukan izin kerja umum. Jalur keluarga dan pensiun harus diperiksa tersendiri. Jangan menyamakan syarat asuransi dan keuangan Non-O, O-A dan O-X."
      ],
      [
        "Bukti dan dokumen luar negeri",
        "Gunakan daftar dari kedutaan atau instansi yang benar-benar menerima permohonan. Paspor, bukti tempat tinggal, dokumen pemberi kerja atau sekolah, hubungan keluarga dan keuangan mempunyai fungsi berbeda; tidak semuanya diperlukan untuk setiap kategori. Jangan langsung memakai daftar negara atau visa lain."
      ],
      [
        "Biaya, waktu dan kewajiban setelah persetujuan",
        "Penawaran sebaiknya memisahkan jasa profesional, biaya resmi, terjemahan dan pihak ketiga. Perkiraan waktu bergantung pada kelengkapan, janji temu, antrean dan pemeriksaan tambahan. Penasihat tidak dapat menjamin tanggal atau hasil persetujuan."
      ]
    ],
    "sections": [
      {
        "heading": "Pilih dasar permohonan sebelum mengumpulkan dokumen",
        "paragraphs": [
          "TR terkait wisata; Non-B dapat terkait bisnis atau pekerjaan, tetapi visa bukan izin kerja umum. Jalur keluarga dan pensiun harus diperiksa tersendiri. Jangan menyamakan syarat asuransi dan keuangan Non-O, O-A dan O-X.",
          "SMART dan LTR perlu pemeriksaan sesuai program BOI terkait. DTV bergantung pada tujuan dan bukti yang memenuhi kategori. Thailand Privilege adalah program keanggotaan, bukan izin tinggal tetap atau izin kerja umum. Baca ketentuan terkini sebelum membayar.",
          "PR berbeda dari perpanjangan tahunan dan kewarganegaraan. Periksa periode penerimaan, kategori, riwayat tinggal dan bukti pada Biro Imigrasi. Lamanya tinggal saja tidak menjamin persetujuan. Pertahankan status sah sesuai arahan instansi selama menunggu."
        ]
      },
      {
        "heading": "Bukti dan dokumen luar negeri",
        "paragraphs": [
          "Gunakan daftar dari kedutaan atau instansi yang benar-benar menerima permohonan. Paspor, bukti tempat tinggal, dokumen pemberi kerja atau sekolah, hubungan keluarga dan keuangan mempunyai fungsi berbeda; tidak semuanya diperlukan untuk setiap kategori. Jangan langsung memakai daftar negara atau visa lain.",
          "Periksa nama, tanggal dan hubungan antar dokumen sebelum penerjemahan. Tanyakan bahasa dan jalur pengesahan kepada penerima. Notaris, legalisasi konsuler dan pengesahan kedutaan memiliki fungsi berbeda dan tidak dengan sendirinya membuktikan kelayakan visa. Kasus BOI memerlukan pemeriksaan proyek, posisi dan orang yang disetujui, bukan anggapan semua pegawai asing tercakup."
        ]
      },
      {
        "heading": "Biaya, waktu dan kewajiban setelah persetujuan",
        "paragraphs": [
          "Penawaran sebaiknya memisahkan jasa profesional, biaya resmi, terjemahan dan pihak ketiga. Perkiraan waktu bergantung pada kelengkapan, janji temu, antrean dan pemeriksaan tambahan. Penasihat tidak dapat menjamin tanggal atau hasil persetujuan.",
          "Bedakan masa berlaku visa dari waktu tinggal yang diberikan saat masuk. Periksa cap masuk, ketentuan kerja, pelaporan alamat, laporan berkala dan masuk kembali sesuai status sebenarnya. Pengecualian dapat berbeda; pengajuan perpanjangan tidak boleh dianggap otomatis mempertahankan izin tinggal."
        ]
      }
    ],
    "faqs": [
      {
        "q": "Dapatkah beralih ke Non-B di Thailand?",
        "a": "Periksa status, sisa izin tinggal, bukti pemberi kerja dan syarat terkini instansi penerima. Tidak tersedia untuk setiap kasus; hindari ambang hari universal atau anggapan semua konsulat menerima permohonan."
      },
      {
        "q": "Apakah Thailand Privilege memberi izin kerja?",
        "a": "Jangan menganggap keanggotaan sebagai izin kerja umum. Nilai kegiatan nyata dan aturan imigrasi serta kerja yang berlaku secara terpisah."
      },
      {
        "q": "Apa yang harus diperiksa keluarga pendamping?",
        "a": "Status pemegang utama dan hubungan harus sesuai program. Non-O biasa, SMART, LTR dan BOI dapat berbeda dalam batas usia, bukti dan proses."
      },
      {
        "q": "Apakah semua dokumen asing harus dilegalisasi?",
        "a": "Tanyakan instansi penerima sebenarnya. Bahasa dan pengesahan bergantung pada dokumen serta permohonan; jangan membeli jalur pengesahan yang belum dikonfirmasi."
      },
      {
        "q": "Apa yang dilakukan setelah penolakan?",
        "a": "Baca alasan dan prosedur yang tersedia. Bukti tambahan, peninjauan atau permohonan baru bergantung pada jalur; tidak ada banding universal atau jaminan persetujuan berikutnya."
      },
      {
        "q": "Bisakah laporan diajukan oleh wakil?",
        "a": "Konfirmasi apakah laporan dan status Anda mengizinkan perwakilan atau pengajuan daring. Sepakati lingkup, kewenangan dan penanganan tanda terima terlebih dahulu."
      }
    ],
    "ctaCall": "Minta penilaian dan penawaran sesuai kasus",
    "ctaLine": "Sampaikan tujuan, negara tempat tinggal dan tanggal izin tinggal melalui halaman kontak kantor.",
    "breadcrumbHome": "Beranda",
    "breadcrumbVisa": "Visa",
    "seeAllServices": "Jalur permohonan visa",
    "seeAllCountries": "Navigasi kewarganegaraan"
  }
};
